import { routes } from "@/helpers";
import {
  useCreateProjectRoleMutation,
  useGetProjectRoleQuery,
  useGetProjectRolesQuery,
  useSaveProjectRoleMutation,
} from "@/services";
import { node } from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export const ProjectRoleContext = createContext();
export const useProjectRoleContext = () => useContext(ProjectRoleContext);

export const ProjectRoleProvider = ({ children }) => {
  const { id } = useParams();
  const intl = useIntl();
  const { data: projectRoleData, isFetching: projectRoleIsLoading } =
    useGetProjectRoleQuery(id, { skip: !id });
  const [saveRole, { isLoading: projectRoleSaving }] =
    useSaveProjectRoleMutation();
  const [createRole, { isLoading: createRoleSaving }] =
    useCreateProjectRoleMutation();
  const { data: projectRolesData } = useGetProjectRolesQuery();

  const [projectRole, setProjectRole] = useState({});
  const [projectRoles, setProjectRoles] = useState([]);
  const [hasErrors, setHasErrors] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setProjectRole(projectRoleData);
  }, [projectRoleData]);

  useEffect(() => {
    setProjectRoles(projectRolesData);
  }, [projectRolesData]);

  const updateRegionalRole = (regionalRole) => {
    const tempRegionRoles = projectRole?.regionalProjectRoles?.map((role) => {
      if (role.id === regionalRole.id) {
        return regionalRole;
      }
      return role;
    });

    const tempRole = { ...projectRole };
    tempRole.regionalProjectRoles = tempRegionRoles;

    setProjectRole(tempRole);
  };

  const createProjectRole = () => {
    createRole(projectRole)
      .unwrap()
      .then((role) => {
        toast.success(
          intl.formatMessage(
            {
              id: "projectRoles.createRoleSuccess",
            },
            {
              name: role.name,
            }
          )
        );
        setProjectRole(role);
        navigate(routes.admin.projectRoles.get(role.id));
      })
      .catch(() => {
        toast.error(
          intl.formatMessage(
            {
              id: "projectRoles.createRoleError",
            },
            {
              name: projectRole.name,
            }
          )
        );
      });
  };

  const updateProjectRole = (role) => {
    setProjectRole({ ...projectRole, ...role });
  };

  const saveProjectRole = () => {
    saveRole(projectRole)
      .unwrap()
      .then(() => {
        toast.success(
          intl.formatMessage({ id: "projectRoles.saveRoleSuccess" })
        );
      })
      .catch(() =>
        toast.error(intl.formatMessage({ id: "projectRoles.saveRoleError" }))
      );
  };

  return (
    <ProjectRoleContext.Provider
      value={{
        projectRole,
        setProjectRole,
        createProjectRole,
        updateProjectRole,
        saveProjectRole,
        projectRoleSaving,
        createRoleSaving,
        projectRoleIsLoading,
        updateRegionalRole,
        hasErrors,
        setHasErrors,
        projectRoles,
      }}
    >
      {children}
    </ProjectRoleContext.Provider>
  );
};

ProjectRoleProvider.propTypes = {
  children: node.isRequired,
};
